package fr.manyverse.app.presentation.components

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Delete
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.SuggestionChip
import androidx.compose.material3.SuggestionChipDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import fr.manyverse.app.domain.model.Character
import fr.manyverse.app.domain.model.Universe
import fr.manyverse.app.presentation.state.AsyncUiState
import fr.manyverse.app.presentation.viewmodel.CharacterViewModel
import manyverse.shared.generated.resources.Res
import manyverse.shared.generated.resources.delete_character
import manyverse.shared.generated.resources.edit_character
import manyverse.shared.generated.resources.universe_custom_fallback
import org.jetbrains.compose.resources.stringResource
import org.koin.compose.viewmodel.koinViewModel

@Composable
fun CharacterCard(
    character: Character,
    universe: Universe? = null,
    onEditCharacter: (String) -> Unit = {},
    viewModel: CharacterViewModel = koinViewModel()
) {
    val deleteState by viewModel.deleteCharacterState.collectAsStateWithLifecycle()

    val universeName = universe?.displayName?.takeIf { it.isNotBlank() }
        ?: stringResource(Res.string.universe_custom_fallback)

    ElevatedCard(
        modifier = Modifier
            .fillMaxWidth()
            .height(240.dp)
            .testTag("characterCard_${character.id}")
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(16.dp),
            horizontalArrangement = Arrangement.spacedBy(16.dp)
        ) {
            // Avatar
            CharacterAvatar(
                imageUrl = character.signedImageUrl,
                characterName = character.name,
                size = 80.dp
            )

            // Character details
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                Text(
                    text = character.name,
                    style = MaterialTheme.typography.titleLarge,
                    color = MaterialTheme.colorScheme.primary,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.testTag("characterName_${character.id}")
                )

                SuggestionChip(
                    onClick = {},
                    label = {
                        Text(
                            text = universeName,
                            style = MaterialTheme.typography.labelSmall
                        )
                    },
                    colors = SuggestionChipDefaults.suggestionChipColors(
                        containerColor = MaterialTheme.colorScheme.secondaryContainer
                    ),
                    modifier = Modifier.testTag("characterUniverseChip_${character.id}")
                )

                HorizontalDivider()

                Text(
                    text = character.story,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurface,
                    maxLines = 3,
                    overflow = TextOverflow.Ellipsis,
                    modifier = Modifier.testTag("characterStory_${character.id}")
                )

                Spacer(modifier = Modifier.weight(1f))

                Row(
                    horizontalArrangement = Arrangement.End,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    IconButton(
                        onClick = { onEditCharacter(character.id) },
                        modifier = Modifier.testTag("editCharacterButton_${character.id}")
                    ) {
                        Icon(
                            Icons.Filled.Edit,
                            contentDescription = stringResource(Res.string.edit_character)
                        )
                    }
                    IconButton(
                        enabled = deleteState !is AsyncUiState.Loading,
                        onClick = { viewModel.requestDeleteCharacter(character) },
                        modifier = Modifier.testTag("deleteCharacterButton_${character.id}")
                    ) {
                        Icon(
                            Icons.Filled.Delete,
                            contentDescription = stringResource(Res.string.delete_character)
                        )
                    }
                }
            }
        }
    }
}
